import * as React from 'react';
import { toast } from 'sonner';
import {
  assetRequestTiming,
  ASSET_REQUEST_STATUS_BLURBS,
  ASSET_REQUEST_STATUS_LABELS,
  formatPlainDate,
  isAssetRequestOpen,
  today,
} from '@cfl/assets-shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { FreeText } from '@/components/free-text';
import { CategoryPicker, EmployeePicker } from '@/components/pickers';
import { ApiError } from '@/lib/api';
import {
  ASSET_REQUEST_TIMING_LABELS,
  assetRequestTimingVariant,
  assetRequestVariant,
  PILL_CLASS,
} from '@/lib/badges';
import {
  useCancelAssetRequest,
  useMyAssetRequests,
  useRaiseAssetRequest,
  type AssetRequest,
} from '@/lib/asset-requests-api';
import { useAssetCategories } from '@/lib/assets-api';
import { useEmployees } from '@/lib/employees-api';
import { useCan, useSession } from '@/lib/session';

/**
 * Asking for hardware, and the list of what you have asked for.
 *
 * Sits on the employee's own screen rather than behind a permission, because the whole
 * point is that anybody can ask - a plain EMPLOYEE, a CONSULTANT. A request moves no
 * asset and reserves nothing, so there is nothing here that needs gating; what IT
 * actually issues is IT's decision, made on the queue.
 */
export function MyAssetRequests() {
  const { data: requests, isPending } = useMyAssetRequests();
  const [asking, setAsking] = React.useState(false);

  const open = requests?.filter((r) => isAssetRequestOpen(r.status)) ?? [];
  const done = requests?.filter((r) => !isAssetRequestOpen(r.status)) ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 pb-3">
          <div>
            <CardTitle className="text-sm">
              Things you have asked for
              {open.length > 0 && (
                <Badge variant="soft-warning" className={`${PILL_CLASS} ml-2`}>
                  {open.length} open
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs">
              Requests you raised, and anything raised for you. Nothing here is reserved — IT
              decides what actually gets issued.
            </CardDescription>
          </div>
          <Button hint={"Ask IT for an asset you need"} size="sm" onClick={() => setAsking(true)}>
            Request an asset
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {isPending ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (requests?.length ?? 0) === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              You have not asked for anything. Need a laptop, a monitor, a chair? Ask here rather
              than messaging IT — this way it does not get lost in a chat.
            </p>
          ) : (
            <ul className="divide-y">
              {[...open, ...done].map((request) => (
                <RequestRow key={request.id} request={request} own />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <RaiseDialog open={asking} onOpenChange={setAsking} />
    </>
  );
}

/**
 * One request, as either side sees it.
 *
 * `own` adds the withdraw button. IT's queue passes `onReview` instead - the same row
 * rather than two near-identical ones, because the facts a reader needs are the same
 * whichever screen they are on.
 */
export function RequestRow({
  request,
  own,
  onReview,
}: {
  request: AssetRequest;
  own?: boolean;
  onReview?: () => void;
}) {
  const cancel = useCancelAssetRequest(request.id);
  const timing = assetRequestTiming(request.neededBy, today());
  const canWithdraw = own && isAssetRequestOpen(request.status);

  const withdraw = async () => {
    try {
      await cancel.mutateAsync();
      toast.success('Request withdrawn.');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be withdrawn.');
    }
  };

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{request.category.name}</span>
        <span className="text-sm text-muted-foreground">for {request.forEmployee.name}</span>

        <Badge variant={assetRequestVariant(request.status)} className={PILL_CLASS}>
          {ASSET_REQUEST_STATUS_LABELS[request.status]}
        </Badge>
        {/* Only while it is still open. On a fulfilled request "date passed" would be
            scolding somebody about a deadline that has already been met. */}
        {isAssetRequestOpen(request.status) && request.neededBy && (
          <Badge variant={assetRequestTimingVariant(timing)} className={PILL_CLASS}>
            {timing === 'LATE'
              ? `needed ${formatPlainDate(request.neededBy)}`
              : `by ${formatPlainDate(request.neededBy)}`}
          </Badge>
        )}

        <div className="ml-auto flex gap-2">
          {onReview && (
            <Button hint={"See this request"} size="sm" variant="secondary" onClick={onReview}>
              Review
            </Button>
          )}
          {canWithdraw && (
            <Button hint={"Withdraw this request"}
              size="sm"
              variant="ghost"
              className="h-7 text-muted-foreground"
              disabled={cancel.isPending}
              onClick={() => void withdraw()}
            >
              {cancel.isPending ? 'Withdrawing…' : 'Withdraw'}
            </Button>
          )}
        </div>
      </div>

      <FreeText text={request.justification} />

      <p className="text-[11px] text-muted-foreground">
        {ASSET_REQUEST_STATUS_BLURBS[request.status]} · asked by {request.requestedBy.name} on{' '}
        {new Date(request.createdAt).toLocaleDateString()}
        {!request.neededBy && ` · ${ASSET_REQUEST_TIMING_LABELS.NONE}`}
      </p>

      {/* What actually went out. This is the answer to "did I ever get my laptop", and
          it exists because FULFILLED cannot be set without a real handover behind it. */}
      {request.fulfilledWith && (
        <div className="rounded-md bg-success/5 p-2.5 text-xs">
          <span className="at-num font-medium">{request.fulfilledWith.asset.code}</span>{' '}
          {request.fulfilledWith.asset.name} — issued
          {request.fulfilledWith.assignedAt
            ? ` ${formatPlainDate(request.fulfilledWith.assignedAt)}`
            : ''}
        </div>
      )}

      {request.reviewNote && (
        <div className="rounded-md bg-muted/60 p-2.5">
          <FreeText
            text={request.reviewNote}
            accent={false}
            prefix={
              <span className="font-medium">{`${request.reviewedByName ?? 'IT'}: `}</span>
            }
          />
        </div>
      )}
    </li>
  );
}

/**
 * Raising one.
 *
 * A category, not an asset: the person asking does not know asset codes and should not
 * have to. Picking the specific laptop is IT's job and is the whole content of review.
 */
function RaiseDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const session = useSession();
  const raise = useRaiseAssetRequest();
  const { data: categories } = useAssetCategories();
  // Requesting for somebody else needs the directory, which a plain employee cannot
  // read. They can still ask for themselves - the picker is simply not offered.
  const canSeeDirectory = useCan('viewRegister');
  const { data: employees } = useEmployees(
    { status: 'ACTIVE', pageSize: 500 },
    { enabled: canSeeDirectory && open },
  );

  const [categoryId, setCategoryId] = React.useState('');
  const [forEmployeeId, setForEmployeeId] = React.useState('');
  const [justification, setJustification] = React.useState('');
  const [neededBy, setNeededBy] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setCategoryId('');
      setForEmployeeId('');
      setJustification('');
      setNeededBy('');
    }
  }, [open]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!categoryId) {
      toast.error('Choose what kind of asset you need.');
      return;
    }

    try {
      await raise.mutateAsync({
        categoryId,
        // Absent means "me", which the API resolves. Sending the caller's own id would
        // work identically but makes the common case depend on the session being loaded.
        forEmployeeId: forEmployeeId || undefined,
        justification: justification.trim(),
        neededBy: neededBy || null,
      });
      toast.success('Request raised. IT has been told.');
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (Object.values(error.fieldErrors ?? {})[0]?.[0] ?? error.message)
          : 'That could not be raised.';
      toast.error(message);
    }
  };

  const me = session.status === 'authenticated' ? session.user.name : 'you';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request an asset</DialogTitle>
          <DialogDescription>
            This reserves nothing — it puts the request on IT&rsquo;s queue with a date and a
            reason, so it can be prioritised against everyone else&rsquo;s instead of living in a
            chat history.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>What do you need?</Label>
            <CategoryPicker
              categories={categories}
              value={categoryId}
              onChange={setCategoryId}
              allLabel="Choose a type"
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              The kind of thing, not a specific item — IT picks which one.
            </p>
          </div>

          {canSeeDirectory && (
            <div className="space-y-1.5">
              <Label>Who is it for?</Label>
              <EmployeePicker
                employees={employees?.employees}
                value={forEmployeeId}
                onChange={setForEmployeeId}
                allLabel={`${me} (me)`}
              />
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Leave as yourself unless you are asking on somebody else&rsquo;s behalf — a joiner
                who has no login yet, for instance.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Needed by (optional)</Label>
            <Input
              type="date"
              value={neededBy}
              onChange={(e) => setNeededBy(e.target.value)}
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              A joining date, a client visit. IT is chased as this approaches, so a real date
              helps you more than leaving it blank.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>What is it for?</Label>
            <Textarea
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Priya joins on the 3rd as a developer and needs a laptop and a second monitor."
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              IT has to weigh this against everyone else&rsquo;s request, so a sentence of
              context decides how it gets prioritised.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Send the request to IT"} type="submit" disabled={raise.isPending || justification.trim().length < 10}>
              {raise.isPending ? 'Sending…' : 'Raise request'}
            </Button>
            {justification.trim().length < 10 && <span className="self-center text-xs text-muted-foreground">Say why it is needed — at least 10 characters</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
